'use strict';

define('vb/private/stateManagement/rules/defaultValueRule',[
  'vb/private/log',
  'vb/private/constants',
  'vb/binding/expression',
  'vb/private/stateManagement/rules/basicRule'],
(Log, Constants, Expression, BasicRule) => {
  const logger = Log.getLogger('/vb/stateManagement/rules/defaultValueRule');

  /**
   * A special rule that only evaluates the defaultValue expression for an interface constant. The Rules Engine takes
   * over the evaluation of the default value, especially if they are part of a lower order extension or base
   * configuration. This is better understood with an example
   *
   * Consider the following case where C depends on B depends on A.
   * Note: bar:r (means it is rules based); bar:v (means it defines a defaultValue).
   *
   * | Constant | Ext A | Ext B | ExtC  || ExtC Rules     | ExtB Rules    | ExtA Rules    |
   * --------------------------------------------------------------------------------------
   * | foo      |   r   |   v   |   -   || x              |  x            | x             |
   * | bar      |   r   |   v   |   r   || undefined [1]  |  "dvB" [2]    |               |
   * | qux      |   v   |   r   |   r   || undefined [1]  |  undefined[2] | "dvA" [3]     |
   *
   * x: foo is never evaluated by rules engine because a higher order extension ExtB has defaultValue configured
   * even though ExtA defines rules for foo
   *
   * During init, When the rules engine runs for 'bar' and 'qux', we start with ExtC rules and work our way down
   * [1] ExtC rules run do not return any values for bar and qux. So we move to ExtB rules
   * [2] before running ExtB rules bar needs to use the defaultValue configured. This class DefaultValueRule is used
   * for this purpose. 'qux', otoh has rules defined but it too returns undefined
   * [3] ExtA rules are run now to get value for qux. Again a DefaultValueRule is run to eval the configured
   * 'defaultValue'.
   *
   * Note: if bar returned undefined (instead of "dv", see [2] above), then this value of undefined is taken as is,
   * because this would be the current behavior of constant and its extensions, all configured with defaultValue.
   */
  class DefaultValueRule extends BasicRule {
    constructor(ruleDef, container, targetExprs = []) {
      super(ruleDef, container, targetExprs);
      this.log = logger;
      this.container = container;
      const availableContexts = container.getAvailableContexts();
      // constant defaultValue prohibit writes in general, but rules based constants also have a special check
      this.defaultValueDefinition = ruleDef.defaultValueDefinition;
      this.overlay = ruleDef.defaultValue;
      this.referencedVars = ruleDef.dependencies || [];
      // the only condition to evaluate defaultValue expression is that the current value of the constant must be
      // undefined.
      const conditionExpr = `{{ ${Object.keys(this.overlay)[0]} === undefined }}`;
      const condExpr = Expression.getExpression(conditionExpr);
      this.condition = Expression.createFromString(condExpr, availableContexts, true);
    }

    /**
     * @param {Object|undefined} contextChange variables and constants whose value changed
     * @param {Object} liveRulesState live rules state object shared across the current rules execution
     * @returns {Object} evaluated values keyed by target expression, or an empty object when the rule should not run
     */
    run(contextChange, liveRulesState) {
      const { externalContexts } = liveRulesState;
      const hasExternalContexts = !!(externalContexts && Object.keys(externalContexts).length);
      // When $withEnv supplies temporary $env values, rebind only top-level expression defaults.
      // Structured defaults keep the same defaultValue behavior used by normal rules.
      const hasTopLevelDefaultValueExpression = !!(this.defaultValueDefinition
        && Object.keys(this.defaultValueDefinition)
          .some((targetExpr) => Expression.isExpression(this.defaultValueDefinition[targetExpr])));
      if (hasExternalContexts && hasTopLevelDefaultValueExpression) {
        const rule = new BasicRule({
          id: this.id,
          overlay: this.defaultValueDefinition,
        }, this.container, this.targetExprs);
        try {
          // Evaluate expression defaults for this $withEnv run without mutating this reusable default rule.
          rule.setupExpressions(contextChange, liveRulesState);
          return rule.run(contextChange, liveRulesState);
        } finally {
          rule.dispose();
        }
      }

      // Literal defaults use the normal defaultValue overlay. For $withEnv runs, contextChange is
      // undefined, so canExecute() applies the fallback without checking the stored constant value.
      this.overlay = this.ruleDef.defaultValue;
      return super.run(contextChange, liveRulesState);
    }

    canExecute(contextChange) {
      // referencedVars:
      // a defaultValue rule can execute only when at least one of its dependent variable's value changes when a
      // contextChange is present.
      // Example, "bar": "{{ $variables.X + $variables.Y }}". constant bar depends on X and Y and when
      // either of these change then re-evaluate bar.

      // condition:
      // The only condition set on a default rule is for the current value to be undefined. When it's not undefined,
      // the default rule eval in skipped.
      //
      // Taking the example from above, let's say the variable X that bar depends on changes, but qux does not
      // depend on it. So the rules are re-executed.
      //
      // | Constant | Ext A | Ext B | ExtC  || Current Value  ||  ExtC Rules   | ExtB Rules  | ExtA Rules || FinalValue|
      // ---------------------------------------------------------------------------------------------------------------
      // | bar      |   r   |   v   |   r   ||  "dvB"         ||  "rvC" [1]    | "rvC"       | "rvC"      |     "rvC"  |
      // | qux      |   v   |   r   |   r   ||  "dvA"         ||  undefined[1] | undefined[2]| x[3]       |     "dvA"  |
      //
      // [1]: ExtC rules evaluates a value for 'bar'. But no value is generated for 'qux'.
      // [2]: ExtB rules first checks if the defaultValue needs to be evaluated for qux and then run rules. There
      // is not defaultValue configured for qux and rules return undefined for qux.
      // [3]: ExtA can use the defaultValue for the constant 'qux'. But the evaluation is skipped because the
      // constant 'qux' already has a value set from a prior run.
      // Final Values on constants are shown in last column
      //
      // Note: behavior in [3] is because a higher order rules value must always be taken over a lower order
      // defaultValue, except when the current value of the constant is undefined
      if (contextChange) {
        return this.referencedVars.length ? super.canExecute(contextChange) : false;
      }

      return true;
    }
  }

  return DefaultValueRule;
});

